import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  Input,
  VStack,
  Heading,
  Text,
  useToast,
  Center,
  Alert,
  AlertIcon,
  PinInput,
  PinInputField,
  HStack,
  Spinner,
  Link,
  Checkbox,
  FormErrorMessage,
  Flex,
  InputGroup,
  InputRightElement,
} from '@chakra-ui/react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import '../types/turnstile.d.ts';
import { Link as RouterLink } from 'react-router-dom';

export default function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [usePasswordAuth, setUsePasswordAuth] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [widgetId, setWidgetId] = useState<string | null>(null);
  const [isCaptchaLoading, setIsCaptchaLoading] = useState(true);
  const [captchaError, setCaptchaError] = useState<string | null>(null);
  const [otp, setOtp] = useState<string>('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [verificationAttempts, setVerificationAttempts] = useState(0);
  const [tabIndex, setTabIndex] = useState(0);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);
  
  // Single captcha ref
  const captchaRef = useRef<HTMLDivElement>(null);
  
  const { signInWithOtp, signIn, signUp, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const scriptLoadedRef = useRef(false);

  // Theme constants
  const primaryColor = '#1DB954';
  const textColor = 'white';
  const textSecondary = 'rgba(255, 255, 255, 0.7)';
  const inputBg = 'rgba(255, 255, 255, 0.08)';
  const borderColor = 'rgba(255, 255, 255, 0.16)';

  // Get the redirect path from location state or default to '/'
  const from = location.state?.from?.pathname || '/';

  // Check URL parameters for tab selection
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    
    if (tabParam === '0' || tabParam === '1') {
      setTabIndex(parseInt(tabParam));
    }
  }, [location.search]);

  // Check if user is already logged in
  useEffect(() => {
    if (user) {
      console.log('User already logged in, redirecting to:', from);
      navigate(from);
    }
  }, [user, navigate, from]);

  // Cleanup function for Turnstile widget
  const cleanupTurnstile = () => {
    // Remove the widget if it exists
    if (window.turnstile && widgetId) {
      try {
        window.turnstile.remove(widgetId);
        console.log('Removed Turnstile widget:', widgetId);
      } catch (e) {
        console.error('Error removing Turnstile widget:', e);
      }
      setWidgetId(null);
    }

    // Clear the container
    if (captchaRef.current) {
      captchaRef.current.innerHTML = '';
    }
  };

  // Load Turnstile script only once
  useEffect(() => {
    // Only load the script if it hasn't been loaded already
    if (scriptLoadedRef.current || document.querySelector('script[src*="turnstile/v0/api.js"]')) {
      scriptLoadedRef.current = true;
      setIsCaptchaLoading(false);
      return;
    }

    console.log('Loading Turnstile script');
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      console.log('Turnstile script loaded successfully');
      scriptLoadedRef.current = true;
      setIsCaptchaLoading(false);
    };
    
    script.onerror = (error) => {
      console.error('Error loading Turnstile script:', error);
      setCaptchaError('Failed to load CAPTCHA. Please refresh the page and try again.');
      setIsCaptchaLoading(false);
    };
    
    document.body.appendChild(script);

    return () => {
      // We don't remove the script on component unmount because other components might need it
    };
  }, []);

  // Initialize Turnstile when script is loaded
  useEffect(() => {
    // Wait until we have both the script loaded and the captcha container
    if (isCaptchaLoading || !window.turnstile || !captchaRef.current) {
      return;
    }

    console.log('Script and container ready, initializing Turnstile');
    
    // Add a small delay to ensure DOM is fully rendered
    const initTimer = setTimeout(() => {
      initializeCaptcha();
    }, 150);

    // Cleanup on unmount
    return () => {
      cleanupTurnstile();
      clearTimeout(initTimer);
    };
  }, [isCaptchaLoading, captchaRef.current]);

  // Separate function to initialize the CAPTCHA that can be called from multiple places
  const initializeCaptcha = () => {
    // Clean up any existing widget before creating a new one
    cleanupTurnstile();

    // Check if captchaRef.current exists
    if (!captchaRef.current) {
      console.error('CAPTCHA container not found');
      return;
    }

    console.log('Initializing new Turnstile widget');
    
    try {
      const id = window.turnstile.render(captchaRef.current, {
        sitekey: '0x4AAAAAABHxImw84H-6Scpx',
        callback: function(token: string) {
          console.log('Captcha verified successfully');
          setCaptchaToken(token);
        },
        'error-callback': function() {
          console.error('Captcha verification failed');
          setCaptchaError('CAPTCHA verification failed. Please try again.');
        },
        theme: 'dark'
      });
      
      setWidgetId(id);
      console.log('New Turnstile widget initialized with ID:', id);
    } catch (error) {
      console.error('Error initializing Turnstile:', error);
      setCaptchaError('Error initializing CAPTCHA. Please refresh the page.');
    }
  };

  const handlePasswordToggle = () => setShowPassword(!showPassword);

  const toggleAuthMethod = () => {
    setUsePasswordAuth(!usePasswordAuth);
    // Reset any errors when switching auth methods
    setCaptchaError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setOtpError(null);

    // Always refresh the captcha when submit is pressed
    // We need to capture the token before resetting
    const currentCaptchaToken = captchaToken;
    
    // Reset captcha for next attempt
    if (window.turnstile && widgetId) {
      window.turnstile.reset(widgetId);
      
      // Queue the token reset for after this function completes
      // to prevent issues during the current submission
      setTimeout(() => {
        setCaptchaToken(null);
      }, 100);
    }

    try {
      // If using password auth (password field is visible and has input)
      if (usePasswordAuth && password) {
        try {
          if (tabIndex === 0) { // Sign in
            await signIn(email, password, currentCaptchaToken || undefined);
            toast({
              title: 'Sign in successful',
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
            // Redirect to the page the user was trying to access or home
            navigate(from);
          } else { // Sign up
            if (!termsAccepted) {
              setShowTermsError(true);
              setIsLoading(false);
              return;
            }
            await signUp(email, password);
            toast({
              title: 'Account created',
              description: 'Please check your email for the confirmation link.',
              status: 'success',
              duration: 5000,
              isClosable: true,
            });
          }
        } catch (error: any) {
          console.error('Authentication error:', error);
          toast({
            title: 'Error',
            description: error.message || 'Authentication failed. Please try again.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
        // We don't return early here anymore, to prevent breaking the finally block
      } else {
        // Otherwise, require captcha for OTP flow
        if (!currentCaptchaToken) {
          toast({
            title: 'Captcha Required',
            description: 'Please complete the captcha verification.',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          setIsLoading(false);
          return;
        }

        // Check if it's a signup and terms are accepted
        if (tabIndex === 1) { // Signup tab
          if (!termsAccepted) {
            setShowTermsError(true);
            setIsLoading(false);
            return;
          }
        }

        console.log('Submitting OTP request with token:', currentCaptchaToken ? 'Present' : 'Not present');
        // For sign in (tab 0), set shouldCreateUser to false; for sign up (tab 1), don't set it (defaults to true)
        if (tabIndex === 0) {
          await signInWithOtp(email, currentCaptchaToken || undefined, false);
        } else {
          await signInWithOtp(email, currentCaptchaToken || undefined);
        }
        setIsEmailSent(true);
        
        toast({
          title: 'Verification Email Sent',
          description: `We've sent a verification code to ${email}`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error: any) {
      console.error('Authentication error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Authentication failed. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) {
      setOtpError('Please enter a 6-digit code');
      return;
    }

    setIsVerifying(true);
    setOtpError(null);
    setVerificationAttempts(prev => prev + 1);

    try {
      console.log('Verifying OTP:', { email, otp });
      
      // Verify OTP with Supabase
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: 'email'
      });

      if (error) {
        console.error('OTP verification error:', error);
        setOtpError(error.message || 'Invalid verification code');
        // Clear OTP input on error for better UX
        setOtp('');
        return;
      }

      console.log('OTP verification successful:', data);
      
      // Verification successful
      toast({
        title: 'Verification successful',
        description: 'You have been logged in successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Short delay to ensure session is fully processed
      setTimeout(() => {
        // Redirect to the page the user was trying to access or home
        navigate(from);
      }, 500);
    } catch (error: any) {
      console.error('Unexpected error during OTP verification:', error);
      setOtpError('An unexpected error occurred. Please try again.');
      setOtp('');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendOTP = async () => {
    setIsLoading(true);
    setOtpError(null);
    try {
      // Pass the same shouldCreateUser parameter as the original request
      if (tabIndex === 0) {
        await signInWithOtp(email, undefined, false);
      } else {
        await signInWithOtp(email);
      }
      toast({
        title: 'Code Resent',
        description: 'A new verification code has been sent to your email.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error('Failed to resend code:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to send a new code. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setIsEmailSent(false);
    setOtp('');
    setOtpError(null);
    setVerificationAttempts(0);
    setUsePasswordAuth(false);
    
    // Re-initialize the captcha after a short delay
    setTimeout(() => {
      if (!isCaptchaLoading && window.turnstile && captchaRef.current) {
        initializeCaptcha();
      }
    }, 150);
  };

  // Handle tab change - reset form when switching
  const handleTabChange = (index: number) => {
    setTabIndex(index);
    setEmail('');
    setPassword('');
    setShowTermsError(false);
    setTermsAccepted(false);
    setCaptchaToken(null);
    setUsePasswordAuth(false);
    
    // Reset and reinitialize captcha with a slight delay to ensure DOM is updated
    setTimeout(() => {
      if (window.turnstile && captchaRef.current) {
        initializeCaptcha();
      }
    }, 150);
  };

  return (
    <Box
      minH="100vh"
      w="100%"
      position="relative"
      bg="#121212"
      overflow="hidden"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      px={4}
    >
      {/* Logo */}
      <Box
        position="absolute"
        top={6}
        left={6}
        zIndex={2}
      >
        <Text
          fontSize="xl"
          fontWeight="bold"
          letterSpacing="wide"
          color="white"
        >
          SyncdIt<sup>®</sup>
        </Text>
      </Box>
      
      {/* Main content */}
      <Box
        maxW="400px"
        w="100%"
        bg="rgba(28, 28, 30, 0.7)"
        backdropFilter="blur(10px)"
        borderRadius="md"
        p={6}
        zIndex={1}
        mx="auto"
      >
        {isEmailSent ? (
          <VStack spacing={8} align="stretch">
            <VStack spacing={3} mb={4}>
              <Box mb={2} display="flex" justifyContent="center">
                <Box
                  w="48px"
                  h="48px"
                  borderRadius="full"
                  bg={primaryColor}
                />
              </Box>
              
              <Heading 
                size="lg" 
                textAlign="center"
                fontWeight="medium"
                color="white"
              >
                Verify Your Email
              </Heading>
              <Text 
                textAlign="center" 
                color={textSecondary}
                fontSize="sm"
              >
                Enter the verification code sent to {email}
              </Text>
            </VStack>
            
            <FormControl>
              <Center>
                <HStack spacing="8px">
                  <PinInput 
                    size="lg" 
                    value={otp} 
                    onChange={setOtp} 
                    otp 
                    placeholder=""
                    isInvalid={!!otpError}
                    autoFocus
                  >
                    <PinInputField 
                      borderColor={borderColor} 
                      _focus={{ borderColor: "white" }} 
                      color="white" 
                      bg={inputBg}
                      h="48px"
                      w="36px"
                      fontSize="lg"
                    />
                    <PinInputField 
                      borderColor={borderColor} 
                      _focus={{ borderColor: "white" }} 
                      color="white" 
                      bg={inputBg}
                      h="48px"
                      w="36px"
                      fontSize="lg"
                    />
                    <PinInputField 
                      borderColor={borderColor} 
                      _focus={{ borderColor: "white" }} 
                      color="white" 
                      bg={inputBg}
                      h="48px"
                      w="36px"
                      fontSize="lg"
                    />
                    <PinInputField 
                      borderColor={borderColor} 
                      _focus={{ borderColor: "white" }} 
                      color="white" 
                      bg={inputBg}
                      h="48px"
                      w="36px"
                      fontSize="lg"
                    />
                    <PinInputField 
                      borderColor={borderColor} 
                      _focus={{ borderColor: "white" }} 
                      color="white" 
                      bg={inputBg}
                      h="48px"
                      w="36px"
                      fontSize="lg"
                    />
                    <PinInputField 
                      borderColor={borderColor} 
                      _focus={{ borderColor: "white" }} 
                      color="white" 
                      bg={inputBg}
                      h="48px"
                      w="36px"
                      fontSize="lg"
                    />
                  </PinInput>
                </HStack>
              </Center>
              {otpError && (
                <Text color="red.400" mt={2} textAlign="center" fontSize="sm">
                  {otpError}
                </Text>
              )}
            </FormControl>

            <Button
              isLoading={isVerifying}
              onClick={handleVerifyOTP}
              isDisabled={otp.length !== 6}
              h="48px"
              borderRadius="md"
              fontWeight="medium"
              bg="white"
              color="black"
              _hover={{ bg: "gray.100" }}
              _active={{ bg: "gray.200" }}
            >
              Continue
            </Button>

            <VStack spacing={2}>
              <Button
                variant="link"
                onClick={handleResendOTP}
                isLoading={isLoading}
                isDisabled={isVerifying}
                color={textSecondary}
                fontSize="sm"
                fontWeight="normal"
                _hover={{ color: "white" }}
              >
                Didn't receive a code? Resend
              </Button>
              
              <Button
                variant="link"
                onClick={handleBackToEmail}
                isDisabled={isVerifying || isLoading}
                size="sm"
                color={textSecondary}
                fontSize="xs"
                fontWeight="normal"
                _hover={{ color: "white" }}
              >
                Use a different email
              </Button>
            </VStack>
          </VStack>
        ) : (
          <VStack spacing={6}>
            <VStack spacing={2}>
              <Box mb={2} display="flex" justifyContent="center">
                <img 
                  src="/assets/logos/logo_only.png" 
                  alt="SyncdIt Logo"
                  width="128px"
                  height="128px"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const iconElement = document.createElement('span');
                    iconElement.innerHTML = '<svg width="28" height="28" viewBox="0 0 24 24" fill="#4299E1"><path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"></path></svg>';
                    e.currentTarget.parentNode?.appendChild(iconElement);
                  }}
                />
              </Box>
              
              <Heading 
                size="lg" 
                textAlign="center"
                fontWeight="medium"
                color="white"
              >
                {tabIndex === 0 ? 'Welcome Back' : 'Welcome to SyncdIt'}
              </Heading>
              <Text 
                textAlign="center" 
                color={textSecondary}
                fontSize="sm"
              >
                {tabIndex === 0 ? 'Sign in to access your account' : 'Begin by creating an account'}
              </Text>
            </VStack>

            {/* Sign in/up buttons */}
            <Flex
              borderRadius="full"
              w="100%"
              maxW="250px"
              bg="rgba(28, 28, 30, 0.5)"
              p="4px"
              mx="auto"
            >
              <Button
                flex="1"
                h="36px"
                fontSize="sm"
                borderRadius="full"
                variant="unstyled"
                bg={tabIndex === 0 ? "white" : "transparent"}
                color={tabIndex === 0 ? "black" : textSecondary}
                onClick={() => handleTabChange(0)}
                fontWeight="medium"
                display="flex"
                justifyContent="center"
              >
                Sign In
              </Button>
              <Button
                flex="1"
                h="36px"
                fontSize="sm"
                borderRadius="full"
                variant="unstyled"
                bg={tabIndex === 1 ? "white" : "transparent"}
                color={tabIndex === 1 ? "black" : textSecondary}
                onClick={() => handleTabChange(1)}
                fontWeight="medium"
                display="flex"
                justifyContent="center"
              >
                Sign Up
              </Button>
            </Flex>

            {/* Form */}
            <Box w="100%" mt={3}>
              <form onSubmit={handleSubmit}>
                <VStack spacing={5}>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoFocus
                    borderColor={borderColor}
                    _hover={{ borderColor: 'rgba(255, 255, 255, 0.3)' }}
                    _focus={{ borderColor: 'white', boxShadow: 'none' }}
                    bg={inputBg}
                    color={textColor}
                    h="48px"
                    fontSize="md"
                    borderRadius="md"
                  />

                  {/* Use Password Authentication Button */}
                  <Button
                    onClick={toggleAuthMethod}
                    variant="ghost"
                    size="sm"
                    color={textSecondary}
                    width="auto"
                    alignSelf="flex-start"
                    _hover={{ color: "white" }}
                    h="auto"
                    p={0}
                    fontSize="sm"
                  >
                    {usePasswordAuth ? 'Hide Password' : 'Use Password'}
                  </Button>

                  {usePasswordAuth && (
                    <InputGroup>
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Password"
                        borderColor={borderColor}
                        _hover={{ borderColor: 'rgba(255, 255, 255, 0.3)' }}
                        _focus={{ borderColor: 'white', boxShadow: 'none' }}
                        bg={inputBg}
                        color={textColor}
                        h="48px"
                        fontSize="md"
                        borderRadius="md"
                      />
                      <InputRightElement h="48px">
                        <Button
                          variant="unstyled"
                          onClick={handlePasswordToggle}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          color={textSecondary}
                          _hover={{ color: "white" }}
                        >
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </Button>
                      </InputRightElement>
                    </InputGroup>
                  )}

                  {usePasswordAuth && (
                    <Button
                      as={RouterLink}
                      to="/forgot-password"
                      variant="ghost"
                      size="sm"
                      color={textSecondary}
                      width="auto"
                      alignSelf="flex-start"
                      _hover={{ color: "white" }}
                      h="auto"
                      p={0}
                      fontSize="sm"
                      mt={1}
                    >
                      Forgot Password?
                    </Button>
                  )}

                  <Center w="100%" py={2} minH="100px">
                    {isCaptchaLoading ? (
                      <VStack>
                        <Spinner size="md" color="white" />
                        <Text color={textSecondary} fontSize="xs">Loading captcha...</Text>
                      </VStack>
                    ) : captchaError ? (
                      <Alert status="error" borderRadius="md" bg="rgba(229, 62, 62, 0.1)" border="1px solid" borderColor="red.300">
                        <AlertIcon />
                        <Text fontSize="sm" color="white">{captchaError}</Text>
                      </Alert>
                    ) : (
                      <Box 
                        ref={captchaRef} 
                        className="cf-turnstile-container" 
                        width="100%" 
                        height="100%" 
                        display="flex" 
                        justifyContent="center"
                      />
                    )}
                  </Center>

                  {tabIndex === 1 && (
                    <FormControl isInvalid={showTermsError && !termsAccepted}>
                      <Checkbox 
                        isChecked={termsAccepted}
                        onChange={(e) => {
                          setTermsAccepted(e.target.checked);
                          if (e.target.checked) setShowTermsError(false);
                        }}
                        borderColor={borderColor}
                        size="sm"
                        colorScheme="green"
                      >
                        <Text fontSize="xs" color={textSecondary}>
                          I accept the{' '}
                          <Link 
                            href="/terms" 
                            color="white"
                            isExternal
                          >
                            Terms
                          </Link>{' '}
                          and{' '}
                          <Link 
                            href="/privacy"    
                            color="white"
                            isExternal
                          >
                            Privacy Policy
                          </Link>
                          . I agree not to use this service while driving or operating any vehicle.
                        </Text>
                      </Checkbox>
                      {showTermsError && !termsAccepted && (
                        <FormErrorMessage fontSize="xs">
                          You must accept the Terms and Privacy Policy
                        </FormErrorMessage>
                      )}
                    </FormControl>
                  )}

                  <Button
                    type="submit"
                    width="full"
                    isLoading={isLoading}
                    isDisabled={
                      (!email) || // Email is always required
                      (tabIndex === 1 && !termsAccepted) || // Terms required for signup
                      // Either password must be provided when using password auth,
                      // or captcha must be valid
                      ((usePasswordAuth && !password) && 
                       (!captchaToken || isCaptchaLoading || !!captchaError))
                    }
                    h="48px"
                    borderRadius="md"
                    fontWeight="medium"
                    bg="white"
                    color="black"
                    _hover={{ bg: "gray.100" }}
                    _active={{ bg: "gray.200" }}
                  >
                    Continue
                  </Button>
                </VStack>
              </form>
            </Box>
          </VStack>
        )}
      </Box>
      
      {/* Footer */}
      <Flex
        position="absolute"
        bottom={6}
        w="100%"
        justifyContent="center"
        zIndex={1}
      >
        <Text fontSize="xs" color="rgba(255, 255, 255, 0.5)" textAlign="center">
          curated by <Text as="span" fontWeight="bold" color="rgba(255, 255, 255, 0.8)">SyncdIt</Text>
        </Text>
      </Flex>
    </Box>
  );
} 